import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useEffect } from 'react';

export default function TechnicalSupportPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleStartChat = () => {
    const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (widget) {
      widget.click();
    }
  };

  const commonIssues = [
    {
      icon: 'ri-camera-off-line',
      title: 'Camera Not Working',
      description: 'Troubleshoot camera connectivity, video quality, or recording issues',
    },
    {
      icon: 'ri-lock-unlock-line',
      title: 'Access Control Problems',
      description: 'Fix keypad, card reader, or door lock malfunctions',
    },
    {
      icon: 'ri-alarm-warning-line',
      title: 'Alarm System Issues',
      description: 'Resolve false alarms, sensor problems, or system errors',
    },
    {
      icon: 'ri-wifi-off-line',
      title: 'Network Connectivity',
      description: 'Fix connection issues between devices and your network',
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Mobile App Issues',
      description: 'Troubleshoot remote access and mobile application problems',
    },
    {
      icon: 'ri-settings-3-line',
      title: 'System Configuration',
      description: 'Get help with settings, zones, and system customization',
    },
  ];

  const quickTips = [
    {
      step: '1',
      title: 'Check Power Supply',
      description: 'Ensure all devices are properly connected to power sources and outlets are working',
    },
    {
      step: '2',
      title: 'Verify Network Connection',
      description: 'Confirm your internet connection is stable and devices are connected to Wi-Fi',
    },
    {
      step: '3',
      title: 'Restart the System',
      description: 'Power cycle your security system by turning it off for 30 seconds, then back on',
    },
    {
      step: '4',
      title: 'Check for Updates',
      description: 'Ensure your system firmware and mobile apps are updated to the latest version',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 bg-coral-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <i className="ri-customer-service-2-line text-4xl text-coral-500"></i>
          </div>
          <h1 className="text-5xl font-bold text-white mb-6">
            AI Technical Support
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Get instant help with your security system. Our AI assistant specializes in troubleshooting and can guide you through common issues 24/7.
          </p>
          <button
            onClick={handleStartChat}
            className="inline-flex items-center gap-3 bg-coral-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-coral-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            <i className="ri-chat-3-line text-xl"></i>
            Start AI Support Chat
          </button>
        </div>
      </section>

      {/* Common Issues Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Common Issues We Can Help With
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our AI assistant is trained to help you troubleshoot these common security system problems
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {commonIssues.map((issue, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-all border border-gray-100 cursor-pointer group"
                onClick={handleStartChat}
              >
                <div className="w-14 h-14 bg-coral-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-coral-500/20 transition-colors">
                  <i className={`${issue.icon} text-2xl text-coral-500`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {issue.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {issue.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Tips Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Quick Troubleshooting Tips
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Try these basic steps before contacting support
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {quickTips.map((tip, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-coral-500 text-white rounded-full flex items-center justify-center text-xl font-bold mb-6">
                  {tip.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {tip.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {tip.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How AI Support Works
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Get help in three simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-coral-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-chat-4-line text-3xl text-coral-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Describe Your Issue
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Tell our AI assistant what problem you're experiencing with your security system
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-coral-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-lightbulb-line text-3xl text-coral-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Get Step-by-Step Guidance
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Receive detailed troubleshooting steps tailored to your specific issue
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-coral-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-checkbox-circle-line text-3xl text-coral-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Resolve the Problem
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Follow the instructions to fix your issue or escalate to a human technician if needed
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Need Human Assistance?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            If the AI assistant can't resolve your issue, our expert technicians are ready to help
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-coral-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-coral-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-xl"></i>
              Contact Support Team
            </Link>
            <Link
              to="/support"
              className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-shield-check-line text-xl"></i>
              View Maintenance Plans
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
